/**
 * Boîte à outils — moteur de simulation partagé par les outils de
 * probabilités (lanceur, arbre de Bayes, planche de Galton). PUR.
 *
 * Tout tirage passe par un générateur `rng: () => number` dans [0 ; 1[ :
 * `Math.random` dans l'app, `seededRng(n)` dans les tests (reproductible).
 */

/** Générateur reproductible (mulberry32) : même graine, mêmes tirages. */
export function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Indice tiré selon des poids positifs (proportionnels aux chances). */
export function pickWeighted(weights: readonly number[], rng: () => number): number {
  const total = weights.reduce((s, w) => s + w, 0);
  let r = rng() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i]!;
    if (r < 0) return i;
  }
  return weights.length - 1;
}

/** Une épreuve de Bernoulli : vrai (succès) avec la probabilité p. */
export function bernoulli(p: number, rng: () => number): boolean {
  return rng() < p;
}

/** n épreuves de Bernoulli indépendantes : le chemin (vrai = succès). */
export function bernoulliPath(n: number, p: number, rng: () => number): boolean[] {
  return Array.from({ length: n }, () => bernoulli(p, rng));
}

/** k parmi n (exact jusqu'à n = 60 environ, ce qui suffit aux outils). */
export function binomCoeff(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  k = Math.min(k, n - k);
  let c = 1;
  for (let i = 1; i <= k; i++) c = (c * (n - k + i)) / i;
  return Math.round(c);
}

/** P(X = k) pour X ~ B(n, p). */
export function binomialPmf(n: number, k: number, p: number): number {
  if (k < 0 || k > n) return 0;
  if (p === 0) return k === 0 ? 1 : 0;
  if (p === 1) return k === n ? 1 : 0;
  return binomCoeff(n, k) * p ** k * (1 - p) ** (n - k);
}

/** Loi B(n, p) entière : [P(X = 0), …, P(X = n)]. */
export function binomialLaw(n: number, p: number): number[] {
  return Array.from({ length: n + 1 }, (_, k) => binomialPmf(n, k, p));
}

/**
 * Échantillonnage de 2nde : [p − 1/√n ; p + 1/√n], borné à [0 ; 1]. Pour
 * n ≥ 25, la fréquence d'un échantillon de taille n y tombe dans au moins
 * 95 % des cas (davantage quand p s'éloigne de 1/2 : 99,5 % pour p = 1/6).
 */
export function fluctuationInterval(p: number, n: number): [number, number] {
  const h = 1 / Math.sqrt(n);
  return [Math.max(0, p - h), Math.min(1, p + h)];
}

/**
 * Indices où lire une suite de longueur `n` pour la tracer avec au plus
 * `points` points, serrés au début (échelle log) : 1, 2, 3, …, puis de plus
 * en plus espacés. Toujours le dernier.
 */
export function logSampleIndices(n: number, points = 160): number[] {
  if (n <= points) return Array.from({ length: n }, (_, i) => i + 1);
  const out = new Set<number>();
  for (let i = 0; i < points; i++) out.add(Math.max(1, Math.round(n ** (i / (points - 1)))));
  out.add(n);
  return [...out].sort((a, b) => a - b);
}
